import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { User, DollarSign, MessageSquare, TrendingUp, TrendingDown, Users, Building, CheckCircle, AlertTriangle, Calendar, ChevronUp, ChevronDown, Layers, FileText, UserCircle } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import MeetingCalendar from './MeetingCalendar';
import ManagerProfile from './ManagerProfile';
import ExportButton from '../common/ExportButton';
import ViewToggle from '../common/ViewToggle';
import { downloadExcel, exportToPDF, getTimestamp } from '../../utils/exportUtils';
import { PERSONA_COLORS } from '../../utils/personaColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerAnalysis = ({ data }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('overview');
  const [sortConfig, setSortConfig] = useState({ key: 'netVariance', direction: 'desc' });
  const [viewMode, setViewMode] = useState('plan'); // 'plan' or 'group'
  const [groupData, setGroupData] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Fetch plan groups when switching to group view
  useEffect(() => {
    if (viewMode === 'group' && groupData.length === 0) {
      setLoadingGroups(true);
      axios.get(`${API}/plan-groups`)
        .then(res => {
          setGroupData(res.data.groups || []);
        })
        .catch(err => console.error('Failed to load plan groups:', err))
        .finally(() => setLoadingGroups(false));
    }
  }, [viewMode, groupData.length]);

  // Calculate manager statistics from individual plans (exclude child plans)
  const managerStats = useMemo(() => {
    const stats = {};
    
    data.filter(c => !c.isChildPlan).forEach(client => {
      const mgr = client.manager || 'Unknown';
      if (!stats[mgr]) {
        stats[mgr] = {
          name: mgr,
          planCount: 0,
          totalRevenue: 0,
          totalWorkloadCost: 0,
          totalMeetingCost: 0,
          totalMessages: 0,
          totalMeetings: 0,
          comMeetings: 0,
          agmMeetings: 0,
          specialMeetings: 0,
          netVariance: 0,
          avgCompliance: 0,
          complianceSum: 0,
          npsScores: [],
          personas: {}
        };
      }
      
      stats[mgr].planCount += 1;
      stats[mgr].totalLots = (stats[mgr].totalLots || 0) + (client.occupiableLots || 0);
      stats[mgr].totalRevenue += client.revenue;
      stats[mgr].totalWorkloadCost += client.workloadCost;
      stats[mgr].totalMeetingCost += client.meetingCost || 0;
      stats[mgr].totalMessages += client.messagesReceived;
      stats[mgr].totalMeetings += client.totalMeetings || 0;
      stats[mgr].comMeetings += client.comMeetings || 0;
      stats[mgr].agmMeetings += client.agmMeetings || 0;
      stats[mgr].specialMeetings += client.specialMeetings || 0;
      stats[mgr].netVariance += client.revWorkloadVariance;
      stats[mgr].complianceSum += client.complianceScore;
      
      if (client.npsScore !== null) {
        stats[mgr].npsScores.push(client.npsScore);
      }
      
      const persona = client.persona || 'Unknown';
      stats[mgr].personas[persona] = (stats[mgr].personas[persona] || 0) + 1;
    });
    
    // Calculate averages and format
    return Object.values(stats).map(mgr => ({
      ...mgr,
      avgCompliance: mgr.planCount > 0 ? mgr.complianceSum / mgr.planCount : 0,
      avgNPS: mgr.npsScores.length > 0 
        ? mgr.npsScores.reduce((a, b) => a + b, 0) / mgr.npsScores.length 
        : null,
      margin: mgr.totalRevenue > 0 ? (mgr.netVariance / mgr.totalRevenue) * 100 : 0,
      profitableClients: Object.entries(mgr.personas)
        .filter(([p]) => ['Model Clients', 'Stable Performers', 'Premium Service', 'Scale Up'].includes(p))
        .reduce((sum, [, count]) => sum + count, 0),
      atRiskClients: Object.entries(mgr.personas)
        .filter(([p]) => ['Optimize', 'Turnaround', 'At Risk', 'Critical Review'].includes(p))
        .reduce((sum, [, count]) => sum + count, 0)
    })).sort((a, b) => b.netVariance - a.netVariance);
  }, [data]);

  // Calculate manager statistics from plan groups
  const managerGroupStats = useMemo(() => {
    const stats = {};
    
    groupData.forEach(group => {
      const mgr = group.primaryManager || group.managers?.[0] || 'Unknown';
      if (!stats[mgr]) {
        stats[mgr] = {
          name: mgr,
          groupCount: 0,
          totalPlanCount: 0,
          totalRevenue: 0,
          totalWorkloadCost: 0,
          totalMeetingCost: 0,
          totalMeetings: 0,
          comMeetings: 0,
          agmMeetings: 0,
          specialMeetings: 0,
          netVariance: 0,
          avgCompliance: 0,
          complianceSum: 0,
          complianceCount: 0,
          personas: {}
        };
      }
      
      stats[mgr].groupCount += 1;
      stats[mgr].totalPlanCount += group.planCount;
      stats[mgr].totalLots = (stats[mgr].totalLots || 0) + (group.totalOccupiableLots || 0);
      stats[mgr].totalRevenue += group.totalRevenue;
      stats[mgr].totalWorkloadCost += group.totalWorkload;
      stats[mgr].totalMeetingCost += group.totalMeetingCost || 0;
      stats[mgr].totalMeetings += group.totalMeetings || 0;
      stats[mgr].comMeetings += group.comMeetings || 0;
      stats[mgr].agmMeetings += group.agmMeetings || 0;
      stats[mgr].specialMeetings += group.specialMeetings || 0;
      stats[mgr].netVariance += group.profit;
      
      if (group.avgCompliance) {
        stats[mgr].complianceSum += group.avgCompliance;
        stats[mgr].complianceCount += 1;
      }
      
      const persona = group.dominantPersona || 'Unknown';
      stats[mgr].personas[persona] = (stats[mgr].personas[persona] || 0) + 1;
    });
    
    // Calculate averages and format
    return Object.values(stats).map(mgr => ({
      ...mgr,
      planCount: mgr.groupCount, // For consistent display
      avgCompliance: mgr.complianceCount > 0 ? mgr.complianceSum / mgr.complianceCount : 0,
      avgNPS: null, // Not available at group level
      margin: mgr.totalRevenue > 0 ? (mgr.netVariance / mgr.totalRevenue) * 100 : 0,
      profitableClients: Object.entries(mgr.personas)
        .filter(([p]) => ['Model Clients', 'Stable Performers', 'Premium Service', 'Scale Up'].includes(p))
        .reduce((sum, [, count]) => sum + count, 0),
      atRiskClients: Object.entries(mgr.personas)
        .filter(([p]) => ['Optimize', 'Turnaround', 'At Risk', 'Critical Review'].includes(p))
        .reduce((sum, [, count]) => sum + count, 0)
    })).sort((a, b) => b.netVariance - a.netVariance);
  }, [groupData]);

  const activeManagerStats = viewMode === 'plan' ? managerStats : managerGroupStats;

  // Sort managers based on config
  const sortedManagers = useMemo(() => {
    return [...activeManagerStats].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [activeManagerStats, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Top managers by various metrics
  const topByRevenue = [...activeManagerStats].sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 10);
  const topByVariance = [...activeManagerStats].sort((a, b) => b.netVariance - a.netVariance).slice(0, 10);
  const bottomByVariance = [...activeManagerStats].sort((a, b) => a.netVariance - b.netVariance).slice(0, 5);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className={`p-4 rounded-xl border shadow-xl min-w-[200px] ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`font-bold text-sm mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{d.name}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Plans:</span> <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{d.planCount}</span></div>
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Revenue:</span> <span className={isDark ? 'text-white' : 'text-slate-900'}>${d.totalRevenue?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Net Variance:</span> <span className={d.netVariance >= 0 ? 'text-emerald-500' : 'text-red-500'}>${d.netVariance?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Margin:</span> <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{d.margin?.toFixed(1)}%</span></div>
          </div>
        </div>
      );
    }
    return null;
  };

  const handleExportManagerExcel = async () => {
    await downloadExcel('managers', `knight_manager_analysis_${getTimestamp()}.xlsx`);
  };

  const handleExportMeetingsExcel = async () => {
    await downloadExcel('meetings', `knight_meetings_${getTimestamp()}.xlsx`);
  };

  const handleExportPDF = async () => {
    await exportToPDF('manager-analysis-content', `knight_manager_analysis_${getTimestamp()}.pdf`, {
      backgroundColor: isDark ? '#0a0a0b' : '#f8fafc'
    });
  };


  const SortHeader = ({ label, sortKey, align = 'left' }) => (
    <th 
      className={`px-5 py-4 cursor-pointer transition-colors border-b group
        ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}
        ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-slate-200 hover:bg-slate-50'}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
        {label}
        <div className={`flex flex-col ${sortConfig.key !== sortKey ? 'opacity-0 group-hover:opacity-30' : ''}`}>
          <ChevronUp className={`w-2 h-2 ${sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'text-emerald-500' : 'text-slate-400'}`} />
          <ChevronDown className={`w-2 h-2 ${sortConfig.key === sortKey && sortConfig.direction === 'desc' ? 'text-emerald-500' : 'text-slate-400'}`} />
        </div>
      </div>
    </th>
  );

  return (
    <div id="manager-analysis-content" className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-2 items-center flex-wrap">
          <button
            onClick={() => setActiveTab('overview')}
            data-testid="manager-overview-tab"
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all
              ${activeTab === 'overview' 
                ? 'bg-emerald-500 text-white' 
                : isDark 
                  ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Manager Overview
          </button>
          <button
            onClick={() => setActiveTab('meetings')}
            data-testid="meeting-calendar-tab"
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all
              ${activeTab === 'meetings' 
                ? 'bg-emerald-500 text-white' 
                : isDark 
                  ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Meeting Calendar
          </button>
          <button
            onClick={() => setActiveTab('profiles')}
            data-testid="manager-profiles-tab"
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all
              ${activeTab === 'profiles' 
                ? 'bg-emerald-500 text-white' 
                : isDark 
                  ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            <UserCircle className="w-4 h-4 inline mr-2" />
            Manager Profiles
          </button>
          
          {activeTab === 'overview' && (
            <div className="ml-4">
              <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
            </div>
          )}
        </div>
        
        <ExportButton 
          onExportExcel={activeTab === 'meetings' ? handleExportMeetingsExcel : handleExportManagerExcel}
          onExportPDF={handleExportPDF}
          excelLabel={activeTab === 'meetings' ? "Export Meetings" : "Export Managers"}
          pdfLabel="Export PDF"
        />
      </div>

      {activeTab === 'meetings' ? (
        <MeetingCalendar data={data} />
      ) : activeTab === 'profiles' ? (
        <ManagerProfile managerStats={managerStats} groupStats={managerGroupStats} clientData={data} />
      ) : loadingGroups && viewMode === 'group' ? (
        <div className={`rounded-2xl border p-12 flex items-center justify-center ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
          <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
        </div>
      ) : (
        <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Managers</span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{activeManagerStats.length}</p>
          {viewMode === 'group' && (
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Managing {groupData.length} plan groups
            </p>
          )}
        </div>
        
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-100'}`}>
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Profitable Managers</span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {activeManagerStats.filter(m => m.netVariance > 0).length}
          </p>
        </div>
        
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
              {viewMode === 'group' ? <Layers className="w-5 h-5 text-amber-500" /> : <Building className="w-5 h-5 text-amber-500" />}
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {viewMode === 'group' ? 'Avg Groups/Manager' : 'Avg Plans/Manager'}
            </span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {activeManagerStats.length > 0 
              ? viewMode === 'group' 
                ? Math.round(groupData.length / activeManagerStats.length)
                : Math.round(data.length / activeManagerStats.length)
              : 0}
          </p>
        </div>
        
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-500/10' : 'bg-purple-100'}`}>
              <CheckCircle className="w-5 h-5 text-purple-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Avg Compliance</span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {activeManagerStats.length > 0 
              ? (activeManagerStats.reduce((sum, m) => sum + m.avgCompliance, 0) / activeManagerStats.length).toFixed(1) 
              : 0}%
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Top Managers by Net Variance */}
        <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
            Top Managers by Gross Margin
          </h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Revenue minus estimated direct workload cost</p>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topByVariance} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                <XAxis type="number" tick={{fontSize: 10, fill: '#64748b'}} axisLine={false} tickLine={false} tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" width={120} tick={{fontSize: 11, fill: isDark ? '#94a3b8' : '#475569'}} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{fill: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}} />
                <Bar dataKey="netVariance" fill="#10b981" radius={[0, 6, 6, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Managers Needing Attention */}
        <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <AlertTriangle className="w-5 h-5 mr-2 text-amber-500" />
            Managers Needing Attention
          </h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Lowest gross profit (potential losses)</p>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bottomByVariance} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                <XAxis type="number" tick={{fontSize: 10, fill: '#64748b'}} axisLine={false} tickLine={false} tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" width={120} tick={{fontSize: 11, fill: isDark ? '#94a3b8' : '#475569'}} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{fill: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}} />
                <Bar dataKey="netVariance" fill="#ef4444" radius={[0, 6, 6, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Manager Table */}
      <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className={`p-5 border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <h3 className={`text-lg font-bold flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {viewMode === 'group' ? <Layers className="w-5 h-5 mr-2 text-blue-500" /> : <User className="w-5 h-5 mr-2 text-blue-500" />}
            Manager Performance Overview
          </h3>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {viewMode === 'group' 
              ? 'Aggregated by plan groups (OC plan with sub-plans combined)'
              : 'Property managers from Plan Status data (includes meeting workload costs)'}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={`text-[11px] uppercase tracking-wider ${isDark ? 'text-slate-500 bg-white/[0.02]' : 'text-slate-500 bg-slate-50'}`}>
              <tr>
                <SortHeader label="Manager" sortKey="name" />
                <SortHeader label={viewMode === 'group' ? 'Groups' : 'Plans'} sortKey="planCount" align="center" />
                {viewMode === 'group' && <SortHeader label="Total Plans" sortKey="totalPlanCount" align="center" />}
                <SortHeader label="Lots" sortKey="totalLots" align="right" />
                <SortHeader label="Revenue" sortKey="totalRevenue" align="right" />
                <SortHeader label="Workload Cost" sortKey="totalWorkloadCost" align="right" />
                <SortHeader label="Meeting Cost" sortKey="totalMeetingCost" align="right" />
                <SortHeader label="Gross Profit" sortKey="netVariance" align="right" />
                <SortHeader label="Gross Margin" sortKey="margin" align="right" />
                <SortHeader label="Meetings" sortKey="totalMeetings" align="center" />
                <SortHeader label="COM" sortKey="comMeetings" align="center" />
                <SortHeader label="AGM" sortKey="agmMeetings" align="center" />
                <SortHeader label="Special" sortKey="specialMeetings" align="center" />
                <SortHeader label="Compliance" sortKey="avgCompliance" align="center" />
                {viewMode === 'plan' && <SortHeader label="Avg NPS" sortKey="avgNPS" align="center" />}
                <SortHeader label="Profitable" sortKey="profitableClients" align="center" />
                <SortHeader label="At Risk" sortKey="atRiskClients" align="center" />
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
              {sortedManagers.map((mgr) => (
                <tr key={mgr.name} className={`transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}`}>
                  <td className={`px-5 py-4 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{mgr.name}</td>
                  <td className={`px-5 py-4 text-center ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{mgr.planCount}</td>
                  {viewMode === 'group' && (
                    <td className={`px-5 py-4 text-center ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{mgr.totalPlanCount}</td>
                  )}
                  <td className={`px-5 py-4 text-right font-mono text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {(mgr.totalLots || 0).toLocaleString()}
                  </td>
                  <td className={`px-5 py-4 text-right font-mono text-xs ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    ${mgr.totalRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </td>
                  <td className={`px-5 py-4 text-right font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    ${mgr.totalWorkloadCost.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </td>
                  <td className={`px-5 py-4 text-right font-mono text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                    ${mgr.totalMeetingCost.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </td>
                  <td className={`px-5 py-4 text-right font-mono text-xs font-bold ${mgr.netVariance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    ${mgr.netVariance.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </td>
                  <td className={`px-5 py-4 text-right font-mono text-xs ${mgr.margin >= 0 ? (isDark ? 'text-slate-300' : 'text-slate-700') : 'text-red-500'}`}>
                    {mgr.margin.toFixed(1)}%
                  </td>
                  <td className={`px-5 py-4 text-center font-mono text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {mgr.totalMeetings.toLocaleString()}
                  </td>
                  <td className={`px-5 py-4 text-center font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {mgr.comMeetings || 0}
                  </td>
                  <td className={`px-5 py-4 text-center font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {mgr.agmMeetings || 0}
                  </td>
                  <td className={`px-5 py-4 text-center font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {mgr.specialMeetings || 0}
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-semibold
                      ${mgr.avgCompliance >= 80 ? 'bg-emerald-500/10 text-emerald-500' : 
                        mgr.avgCompliance >= 60 ? 'bg-amber-500/10 text-amber-500' : 
                        'bg-red-500/10 text-red-500'}`}>
                      {mgr.avgCompliance.toFixed(0)}%
                    </span>
                  </td>
                  {viewMode === 'plan' && (
                    <td className={`px-5 py-4 text-center font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      {mgr.avgNPS !== null ? mgr.avgNPS.toFixed(1) : '-'}
                    </td>
                  )}
                  <td className="px-5 py-4 text-center">
                    <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-500/10 text-emerald-500">
                      {mgr.profitableClients}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${mgr.atRiskClients > 0 ? 'bg-red-500/10 text-red-500' : (isDark ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400')}`}>
                      {mgr.atRiskClients}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={`p-4 border-t text-xs font-medium text-center uppercase tracking-wider ${isDark ? 'border-white/5 bg-white/[0.01] text-slate-500' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
          {activeManagerStats.length} Managers {viewMode === 'group' && `· ${groupData.length} Plan Groups`}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default ManagerAnalysis;
